import React, { useState } from 'react';
import { Button, Input } from '../components/UI';
import { AlertCircle, UserPlus, ArrowLeft, Flag } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { apiRequest } from '../utils/api';
import { useLeague } from '../components/LeagueContext';
import { API_BASE_URL } from '../config';

export const Register: React.FC = () => {
  const navigate = useNavigate();
  const { settings } = useLeague();
  const [formData, setFormData] = useState({ username: '', password: '', confirmPassword: '', eaId: '' });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (formData.password !== formData.confirmPassword) return setError('Die Passwörter stimmen nicht überein.');
    if (formData.password.length < 6) return setError('Das Passwort muss mindestens 6 Zeichen lang sein.');
    setLoading(true);
    try {
      await apiRequest('register.php', 'POST', { username: formData.username, password: formData.password, eaId: formData.eaId });
      alert('Registrierung erfolgreich! Du kannst dich jetzt anmelden.');
      navigate('/login');
    } catch (caught: any) {
      setError(caught.message || 'Registrierung fehlgeschlagen.');
    } finally {
      setLoading(false);
    }
  };

  const getLogoUrl = () => {
    if (!settings?.league_logo_url) return null;
    if (settings.league_logo_url.startsWith('http') || settings.league_logo_url.startsWith('data:')) return settings.league_logo_url;
    const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
    return baseUrl ? `${baseUrl}/${settings.league_logo_url}` : settings.league_logo_url;
  };

  const logoUrl = getLogoUrl();
  const leagueName = settings?.league_name || 'Sunday Driver Cup';

  return (
    <div className="sdc-login-grid min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-10">
      <div className="relative z-10 w-full max-w-xl animate-fade-in">
        <div className="sdc-login-card overflow-hidden rounded-[1.75rem]">
          <div className="sdc-brand-line" />
          <div className="p-6 sm:p-10">
            <Link to="/login" className="mb-8 inline-flex items-center text-[11px] font-bold uppercase tracking-[0.14em] text-white/30 transition-colors hover:text-white"><ArrowLeft size={14} className="mr-2"/> Zurück zur Anmeldung</Link>
            <div className="mb-8 flex items-start gap-4">
              <div className="flex h-14 min-w-14 items-center justify-center rounded-2xl border border-white/[0.08] bg-white/[0.035] p-2">
                {logoUrl ? <img src={logoUrl} alt={`${leagueName} Logo`} className="max-h-10 max-w-[8rem] object-contain" /> : <Flag size={21} className="text-f1-red" />}
              </div>
              <div><p className="sdc-eyebrow mb-1">{leagueName}</p><h1 className="text-3xl font-bold tracking-tight text-white">Startplatz sichern</h1><p className="mt-1 text-sm text-white/40">Erstelle dein persönliches Fahrerprofil.</p></div>
            </div>
            {error && <div role="alert" className="mb-5 flex items-start gap-3 rounded-xl border border-red-500/30 bg-red-500/10 p-3.5 text-sm text-red-100"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />{error}</div>}
            <form onSubmit={handleSubmit}>
              <Input label="Fahrername" value={formData.username} onChange={event => setFormData({...formData, username: event.target.value})} placeholder="Name auf der Homepage" autoComplete="username" autoFocus required />
              <Input label="EA-ID / PSN / Gamertag (optional)" value={formData.eaId} onChange={event => setFormData({...formData, eaId: event.target.value})} placeholder="Dein Name im Spiel" />
              <Input label="Passwort" type="password" value={formData.password} onChange={event => setFormData({...formData, password: event.target.value})} placeholder="Mindestens 6 Zeichen" autoComplete="new-password" required />
              <Input label="Passwort bestätigen" type="password" value={formData.confirmPassword} onChange={event => setFormData({...formData, confirmPassword: event.target.value})} placeholder="Passwort wiederholen" autoComplete="new-password" required />
              <Button className="w-full py-3 mt-2" disabled={loading} icon={UserPlus}>{loading ? 'Account wird erstellt …' : 'Fahrer-Account erstellen'}</Button>
            </form>
            <div className="mt-7 text-center text-[11px] leading-5 text-white/25">Mit der Registrierung gelten das Liga-Regelwerk und die Fair-Play-Grundsätze.</div>
          </div>
        </div>
      </div>
    </div>
  );
};
